<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['view_loan_products'] == 1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">

            <!-- Top Header & Search/Add Actions -->
            <div class="row mb-4 align-items-center">
              <div class="col-sm-12 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="d-inline-flex align-items-center mb-0 me-3">
                    Show
                    <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
                      :options="Options"></b-form-select>
                    entries
                  </label>
                  <div class="d-inline-flex align-items-center mx-3">
                    Search:
                    <b-form-input v-model="filter" type="search" placeholder="Search rules..."
                      class="form-control form-control-sm ms-2"></b-form-input>
                  </div>
                </div>
              </div>

              <div class="col-sm-12 col-md-6 text-md-end mt-3 mt-md-0">
                <b-button size="sm" variant="primary" v-b-modal.create-fine v-if="List > 0 || isBusy">
                  <i class="mdi mdi-plus"></i> New Fine Rule
                </b-button>
              </div>

            </div>

            <!-- Table -->
            <div class="table-responsive">
              <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
                :filter="filter" :filter-included-fields="filterOn" @filtered="Filtered" :busy="IsBusy" show-empty
                class="align-middle table-nowrap table-hover">

                <!-- Loading State -->
                <template #table-busy>
                  <div class="text-center text-primary my-5">
                    <b-spinner class="align-middle" type="grow"></b-spinner>
                    <div class="mt-2 fw-medium">Loading Fine Rules...</div>
                  </div>
                </template>

                <!-- Empty State (No Data) -->
                <template #empty>
                  <div class="text-center py-5">
                    <div class="mb-3">
                      <i class="mdi mdi-text-box-remove-outline text-muted" style="font-size: 64px;"></i>
                    </div>
                    <h5 class="text-dark">No Fine Rules Available</h5>
                    <p class="text-muted mb-4">You haven't added any loan fine rules to the system yet.</p>
                    <b-button variant="primary" v-b-modal.create-fine class="px-4">
                      <i class="mdi mdi-plus me-1"></i> Add Your First Fine Rule
                    </b-button>
                  </div>
                </template>

                <template #cell(index)="data">
                  {{ data.index + 1 }}
                </template>

                <template #cell(type)="data">
                  <span class="fw-bold text-dark">{{ formatType(data.item.type) }}</span>
                </template>

                <template #cell(fixed_amount)="data">
                  <span v-if="data.item.fixed_amount">{{ commas(data.item.fixed_amount) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>

                <template #cell(percentage)="data">
                  <span v-if="data.item.percentage">
                    <b-badge variant="soft-info" class="font-size-12">{{ data.item.percentage }}%</b-badge>
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>

                <template #cell(status)="data">
                  <b-badge class="p-1 fw-bold"
                    :class="{ 'bg-primary': data.item.status == 'ACTIVE', 'bg-secondary': data.item.status == 'INACTIVE' }">
                    {{ data.item.status }}
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <div class="d-flex gap-2">
                    <b-button v-if="data.item.status == 'INACTIVE'" title="Activate" size="sm" variant="primary"
                      @click="promptActivateFine(data.item.id)"><i class="mdi mdi-check-bold"></i></b-button>
                    <b-button v-else title="Deactivate" size="sm" variant="secondary"
                      @click="promptDeactivateFine(data.item.id)"><i class="mdi mdi-close"></i></b-button>
                    <b-button title="Delete" size="sm" variant="danger" @click="promptDeleteFine(data.item.id)"><i
                        class="mdi mdi-trash-can"></i></b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Pagination -->
            <div class="row align-items-center mt-3" v-if="List > 0">
              <div class="col-sm-12 col-md-6">
                <div class="text-muted">
                  Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, TotalRows) }} of
                  {{ TotalRows }} entries
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="TotalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Permission Denied -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div class="text-center py-5 card card-body shadow-sm">
            <img src="@/assets/images/profile-img.png" alt="No Permission" class="img-fluid mx-auto d-block"
              style="max-width: 200px;" /><br>
            <h5 class="text-primary mt-3">Access Denied</h5>
            <span class="text-muted">You do not have the required permissions to view Loan Fines.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Fine Modal -->
    <b-modal id="create-fine" title="Create Loan Fine Rule" hide-footer centered>
      <form @submit.prevent="createFine">
        <div class="form-group mb-4">
          <label class="form-label">Fine Type</label>
          <b-form-select v-model="newFine.type" :options="fineTypeOptions" class="form-select" required></b-form-select>

          <div class="form-text text-muted mt-2 px-1" v-if="newFine.type">
            <i class="mdi mdi-information-outline text-info me-1"></i>
            <span>{{ fineTypeDescriptions[newFine.type] }}</span>
          </div>
        </div>

        <div class="form-group mb-4" v-if="['DAY_FIXED', 'FIXED_INSTALLMENT'].includes(newFine.type)">
          <label class="form-label">Fixed Amount ({{ userData.currency }})</label>
          <input v-number-format type="text" inputmode="numeric" v-model="newFine.fixed_amount" class="form-control"
            placeholder="e.g 1,000" required>
        </div>

        <div class="form-group mb-4" v-if="['DAY_PERCENTAGE', 'PERCENTAGE_INSTALLMENT'].includes(newFine.type)">
          <label class="form-label">Percentage (%)</label>
          <input type="number" v-model="newFine.percentage" class="form-control" step="0.1" max="100"
            placeholder="e.g 2.5" required>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button type="button" variant="light" @click="$bvModal.hide('create-fine')"
            class="me-2 px-4">Cancel</b-button>
          <b-button type="submit" variant="primary" class="px-4" :disabled="isActionLoading">
            <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-content-save me-1"></i>
            {{ isActionLoading ? 'Saving...' : 'Save Rule' }}</b-button>
        </div>
      </form>
    </b-modal>

    <!-- Confirmation Modals -->

    <!-- Activate Modal -->
    <b-modal id="activate-fine-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-primary mb-2">
          <i class="mdi mdi-check-circle-outline"></i>
        </div>
        <h4 class="text-primary mb-3">Activate Fine Rule?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to <strong>activate</strong> this fine rule? <br>
          Activating this rule makes it available to be used on loan
          products.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('activate-fine-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="primary" @click="executeActivateFine" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-check-bold me-1"></i> {{ isActionLoading ? 'Activating...' : 'Yes, Activate' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Deactivate Modal -->
    <b-modal id="deactivate-fine-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-secondary mb-2">
          <i class="mdi mdi-close-circle-outline"></i>
        </div>
        <h4 class="text-secondary mb-3">Deactivate Fine Rule?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to <strong>deactivate</strong> this fine rule? <br>
          Deactivating this rule makes it unavailable for use on loan products.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('deactivate-fine-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="secondary" @click="executeDeactivateFine" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-close me-1"></i> {{ isActionLoading ? 'Deactivating...' : 'Yes, Deactivate' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal id="delete-fine-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-danger mb-2">
          <i class="mdi mdi-trash-can-outline"></i>
        </div>
        <h4 class="text-danger mb-3">Delete Fine Rule?</h4>
        <p class="fs-6 text-muted mb-4">
          This deletes the rule permanently from the system configuration. <br> Records already linked to this rule will
          remain unaffected.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('delete-fine-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="danger" @click="executeDeleteFine" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-trash-can me-1"></i> {{ isActionLoading ? 'Deleting...' : 'Delete' }}
        </b-button>
      </div>
    </b-modal>

  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { loanfinesComputed, loanfinesMethods } from "@/state/helpers";

export default {
  page: {
    title: "Loan Fines Rules",
    meta: [{ name: "description", content: appConfig.description, },],
  },
  components: {
    Layout, PageHeader
  },
  directives: {
    numberFormat: {
      bind(el) {
        function handler() {
          if (el._formatting) return;
          const digits = el.value.replace(/\D/g, '');
          const formatted = digits ? parseInt(digits, 10).toLocaleString('en-US') : '';
          if (el.value !== formatted) {
            el._formatting = true;
            el.value = formatted;
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el._formatting = false;
          }
        }
        el.addEventListener('input', handler);
        el._numberFormatHandler = handler;
      },
      unbind(el) {
        el.removeEventListener('input', el._numberFormatHandler);
      }
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Loan Fine Rules",
      items: [{ text: "Dashboard", to: "/", }, { text: "Loan Fines", active: true, },],
      TotalRows: 0,
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: null,
      filterOn: [],
      isBusy: true,
      isActionLoading: false,
      selectedTargetId: null, // Stores the ID of the rule being acted upon
      fields: [
        { key: 'index', label: '#', visible: true, class: 'align-middle' },
        { key: 'id', label: 'ID', visible: false },
        { key: 'type', label: 'Fine Type', visible: true, class: 'align-middle' },
        { key: 'fixed_amount', label: 'Fixed Amount', visible: true, class: 'align-middle' },
        { key: 'percentage', label: 'Percentage', visible: true, class: 'align-middle' },
        { key: 'status', label: 'Status', visible: true, class: 'align-middle' },
        { key: 'actions', label: 'Actions', visible: true, class: 'align-middle' },
      ],
      newFine: {
        type: 'DAY_FIXED',
        fixed_amount: null,
        percentage: null
      },
      fineTypeOptions: [
        { value: 'DAY_FIXED', text: 'Daily Fixed Fine' },
        { value: 'DAY_PERCENTAGE', text: 'Daily Percentage Fine' },
        { value: 'FIXED_INSTALLMENT', text: 'Fixed Installment Fine' },
        { value: 'PERCENTAGE_INSTALLMENT', text: 'Percentage Installment Fine' }
      ],
      fineTypeDescriptions: {
        'DAY_FIXED': 'Daily Fixed fine is added on a loan every day that passes past the loan expiry date or after the grace period. It is calculated on the loan amount only.',
        'DAY_PERCENTAGE': 'Daily Percentage fine adds a percentage of the outstanding amount to the loan balance for every day past the loan expiry date or grace period.',
        'FIXED_INSTALLMENT': 'Fixed Installment fine is a flat penalty amount applied every time a scheduled repayment installment is missed or delayed.',
        'PERCENTAGE_INSTALLMENT': 'Percentage Installment fine applies a percentage penalty based on the specific missed scheduled repayment installment amount.'
      }
    };
  },
  computed: {
    ...loanfinesComputed,
    List() { return this.loanfines ? this.loanfines.length : 0; },
    Fields() { return this.fields.filter(field => field.visible) },
    Items() { return this.loanfines ? this.loanfines.map((d) => ({ ...d })) : [] },
    IsBusy() { return this.isBusy; },
  },
  async mounted() {
    await this.fetchLoanFines();
    this.TotalRows = this.List;
    this.isBusy = false;
  },
  watch: {
    List(newVal) {
      this.TotalRows = newVal;
    }
  },
  methods: {
    ...loanfinesMethods,
    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0; },
    formatType(type) {
      if (!type) return '';
      const types = {
        'DAY_FIXED': 'Daily Fixed Fine',
        'DAY_PERCENTAGE': 'Daily Percentage Fine',
        'FIXED_INSTALLMENT': 'Fixed Installment Fine',
        'PERCENTAGE_INSTALLMENT': 'Percentage Installment Fine'
      };
      return types[type] || type;
    },
    Filtered(filteredItems) {
      this.TotalRows = filteredItems.length;
      this.currentPage = 1;
    },
    async createFine() {
      this.isActionLoading = true;
      try {
        const payload = {
          ...this.newFine,
          fixed_amount: this.newFine.fixed_amount
            ? Number(String(this.newFine.fixed_amount).replace(/,/g, ''))
            : null
        };
        await axios.post('/add-loan-fine', payload);
        this.$bvModal.hide('create-fine');
        this.newFine = { type: 'DAY_FIXED', fixed_amount: null, percentage: null };
        this.$toast.success('Fine rule created successfully!');

        this.isBusy = true;
        await this.fetchLoanFines();
        this.isBusy = false;
      } catch (error) {
        this.$toast.error('Failed to create fine rule. Please try again.');
      } finally {
        this.isActionLoading = false;
      }
    },

    // Modal Trigger Prompts
    promptActivateFine(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('activate-fine-modal');
    },
    promptDeactivateFine(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('deactivate-fine-modal');
    },
    promptDeleteFine(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('delete-fine-modal');
    },

    // Execution Methods
    async executeActivateFine() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/activate-loan-fine`, obj);
        if (response.status == 200) {
          this.$toast.success('Fine rule activated successfully!');
          this.isBusy = true;
          await this.fetchLoanFines();
          this.isBusy = false;
        } else {
          this.$toast.error('Failed to activate the fine rule.');
        }
      } catch (error) {
        this.$toast.error('Failed to activate the fine rule.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('activate-fine-modal');
      }
    },

    async executeDeactivateFine() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/deactivate-loan-fine`, obj);
        if (response.status == 200) {
          this.$toast.success('Fine rule deactivated successfully!');
          this.isBusy = true;
          await this.fetchLoanFines();
          this.isBusy = false;
        } else {
          this.$toast.error('Failed to deactivate the fine rule.');
        }
      } catch (error) {
        this.$toast.error('Failed to deactivate the fine rule.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('deactivate-fine-modal');
      }
    },

    async executeDeleteFine() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/delete-loan-fine`, obj);
        if (response.status == 200) {
          this.$toast.success('Fine rule deleted successfully!');
          this.isBusy = true;
          await this.fetchLoanFines();
          this.isBusy = false;
        } else {
          this.$toast.error('Failed to delete the fine rule.');
        }
      } catch (error) {
        this.$toast.error('Failed to delete the fine rule.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('delete-fine-modal');
      }
    }
  },
};
</script>